import { toast } from 'react-toastify';

import { getSerializedErrorMessage } from '../../../../../utils/errors';
import { finishTreatmentPlanV3Mutation, startTreatmentPlanV3Mutation } from '../../../utils';
import { isForLoadedAssessmentPage, markAssessmentPageChanged } from './utils';

import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import type { TreatmentPlanV3State } from '../../../types';
import type {
  AssessmentMutationAction,
  CreateTreatmentPlanV3AssessmentBehaviorPayload,
  DeleteTreatmentPlanV3AssessmentBehaviorPayload,
  ITreatmentPlanV3AssessmentBehavior,
  TreatmentPlanV3AssessmentThunks,
  UpdateTreatmentPlanV3AssessmentBehaviorPayload,
} from '../types';

export function addCreateTreatmentPlanV3AssessmentBehaviorCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3AssessmentThunks,
) {
  const { createTreatmentPlanV3AssessmentBehavior } = thunks;
  const flag = 'createAssessmentBehaviorLoading';

  builder
    .addCase(createTreatmentPlanV3AssessmentBehavior.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      createTreatmentPlanV3AssessmentBehavior.fulfilled,
      (
        state,
        action: AssessmentMutationAction<
          ITreatmentPlanV3AssessmentBehavior,
          CreateTreatmentPlanV3AssessmentBehaviorPayload
        >,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedAssessmentPage(state, action) || !state.assessmentPage) return;
        markAssessmentPageChanged(state);
        state.assessmentPage.behaviors.push(action.payload);
      },
    )
    .addCase(createTreatmentPlanV3AssessmentBehavior.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedAssessmentPage(state, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}

export function addUpdateTreatmentPlanV3AssessmentBehaviorCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3AssessmentThunks,
) {
  const { updateTreatmentPlanV3AssessmentBehavior } = thunks;
  const flag = 'updateAssessmentBehaviorLoading';

  builder
    .addCase(updateTreatmentPlanV3AssessmentBehavior.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      updateTreatmentPlanV3AssessmentBehavior.fulfilled,
      (
        state,
        action: AssessmentMutationAction<
          ITreatmentPlanV3AssessmentBehavior,
          UpdateTreatmentPlanV3AssessmentBehaviorPayload
        >,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedAssessmentPage(state, action)) return;
        const list = state.assessmentPage?.behaviors;
        if (!list) return;
        const index = list.findIndex((row) => row.id === action.payload.id);
        if (index === -1) return;
        markAssessmentPageChanged(state);
        list[index] = action.payload;
      },
    )
    .addCase(updateTreatmentPlanV3AssessmentBehavior.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedAssessmentPage(state, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}

export function addDeleteTreatmentPlanV3AssessmentBehaviorCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3AssessmentThunks,
) {
  const { deleteTreatmentPlanV3AssessmentBehavior } = thunks;
  const flag = 'deleteAssessmentBehaviorLoading';

  builder
    .addCase(deleteTreatmentPlanV3AssessmentBehavior.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      deleteTreatmentPlanV3AssessmentBehavior.fulfilled,
      (
        state,
        action: AssessmentMutationAction<void, DeleteTreatmentPlanV3AssessmentBehaviorPayload>,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedAssessmentPage(state, action) || !state.assessmentPage) return;
        markAssessmentPageChanged(state);
        state.assessmentPage.behaviors = state.assessmentPage.behaviors.filter(
          (row) => row.id !== action.meta.arg.behaviorId,
        );
      },
    )
    .addCase(deleteTreatmentPlanV3AssessmentBehavior.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedAssessmentPage(state, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}
